import React, { useState, useEffect } from 'react';
import { Table, Badge, Space, Button, message, Tag, Typography, Card, Select, Tooltip } from 'antd';
import { EyeOutlined, CommentOutlined, UserOutlined } from '@ant-design/icons';
import { SUPPORT_API } from '../constants/api';
import axiosInstance from '../services/axiosConfig';
import SupportDetail from './SupportDetail';
import ActionButtons from '../components/common/ActionButtons';
const { Title } = Typography;

const SupportList = () => {
  const [supports, setSupports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedSupport, setSelectedSupport] = useState(null);

  
  const loadSupports = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(SUPPORT_API.LIST);
      // Lấy data từ response.data.feedbacks
      const data = response?.data?.feedbacksWithUnreadCount || [];
      setSupports(data);
    } catch (error) {
      console.error('Load supports error:', error);
      message.error('Không thể tải danh sách hỗ trợ');
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    // Chỉ gọi loadSupports một lần khi component mount
    loadSupports();
  
    // Có thể thêm interval để tự động refresh sau một khoảng thời gian
    const intervalId = setInterval(() => {
      loadSupports();
    }, 30000); // Refresh mỗi 30 giây
  
    // Cleanup function
    return () => {
      clearInterval(intervalId);
    };
  }, []);
  
  // ...existing code...

  useEffect(() => {
    loadSupports();
  }, []);

  const handleStatusChange = async (feedbackId, newStatus) => {
    try {
      await axiosInstance.put(`${SUPPORT_API.UPDATE(feedbackId)}`, {
        status: newStatus
      });
      message.success('Cập nhật trạng thái thành công');
      loadSupports();
    } catch (error) {
      console.error('Update status error:', error);
      message.error('Không thể cập nhật trạng thái');
    }
  };
  const columns = [
  {
    title: 'Tiêu đề',
    dataIndex: 'Title',
    key: 'title',
    width: '10%',
    render: (text, record) => (
      <Space align="center">
        <CommentOutlined style={{ color: '#1890ff' }} />
        <span style={{ fontWeight: 500 }}>{text}</span>
        {record.unreadMessageCount > 0 && (
          <Badge 
            count={record.unreadMessageCount} 
            style={{ 
              backgroundColor: '#ff4d4f',
              marginLeft: 8 
            }} 
          />
        )}
        {record.unreadCount > 0 && (
          <Badge 
            count={record.unreadCount} 
            style={{ 
              backgroundColor: '#52c41a',
              marginLeft: 4 
            }} 
          />
        )}
      </Space>
    ),
},
  {
    title: 'Người gửi',
    dataIndex: 'UserID',
    key: 'user',
    width: '25%',
    render: (_, record) => (
      <Tooltip
          title={
            <div>
              <div>{record.Tenant?.TenantName}</div>
              <div>SĐT: {record.Tenant?.Phone}</div>
              <div>Email: {record.Tenant?.Email}</div>
            </div>
          }
        >
          <Tag icon={<UserOutlined />} color="orange" style={{ margin: 0 }}>
                  {record.Tenant?.TenantName || record.Tenant?.TenantName}
                </Tag>
        </Tooltip>
    ),
  },
  {
    title: 'Nội dung',
    dataIndex: 'Content',
    key: 'content',
    width: '25%', // Reduced width to accommodate new column
    ellipsis: true,
  },
    {
      title: 'Trạng thái',
      dataIndex: 'Status',
      key: 'status',
      width: '15%',
      render: (status) => {
        const statusConfig = {
          pending: {
            color: 'gold',
            text: 'Chờ xử lý'
          },
          'in-progress': {
            color: 'blue',
            text: 'Đang xử lý'
          },
          resolved: {
            color: 'green',
            text: 'Đã giải quyết'
          },
          rejected: {
            color: 'red',
            text: 'Từ chối'
          }
        };

        const currentStatus = statusConfig[status?.toLowerCase()] || {
          color: 'default',
          text: 'Không xác định'
        };

        return (
          <Tag color={currentStatus.color} style={{ minWidth: 90, textAlign: 'center' }}>
            {currentStatus.text}
          </Tag>
        );
      },
    },
    {
      title: 'Ngày tạo',
      dataIndex: 'CreatedAt',
      key: 'createdAt',
      width: '15%',
      render: (date) => {
        if (!date) return '-';
        
        try {
          // Parse thời gian từ chuỗi ISO "2025-05-23T13:42:43.533Z"
          const dateStr = date.toString();
          
          if (dateStr.includes('T')) {
            // Xử lý format ISO: "2025-05-23T13:42:43.533Z"
            const [datePart, timePart] = dateStr.split('T');
            const timeOnly = timePart.split('.')[0]; // Bỏ milliseconds và Z
            
            const [year, month, day] = datePart.split('-');
            const [hour, minute] = timeOnly.split(':');
            
            // Format lại theo định dạng Việt Nam với thời gian UTC gốc
            return `${hour}:${minute} ${day}/${month}/${year}`;
          } else {
            // Xử lý format thường: "2025-05-23 13:42:43.533"
            const parts = dateStr.split(' ');
            if (parts.length >= 2) {
              const datePart = parts[0]; // "2025-05-23"
              const timePart = parts[1].split('.')[0]; // "13:42:43"
              
              const [year, month, day] = datePart.split('-');
              const [hour, minute] = timePart.split(':');
              
              return `${hour}:${minute} ${day}/${month}/${year}`;
            }
          }
          
          // Fallback về cách cũ nếu format khác
          const dateObj = new Date(date);
          return dateObj.toLocaleString('vi-VN', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
          });
        } catch (error) {
          console.error('Date parsing error:', error);
          return '-';
        }
      },
    },
    {
      title: 'Hành động',
      key: 'action',
      width: '15%',
      align: 'center',
      render: (_, record) => (
        <Space>
          <ActionButtons
            module="supports"
            onView={() => setSelectedSupport(record)}
            showAdd={false}
            showEdit={false}
            showDelete={false}
            showPay={false}
            record={record}
          />
          <Select
            value={record.Status}
            style={{ width: 120 }}
            onChange={(value) => handleStatusChange(record.FeedbackID, value)}
          >
            <Select.Option value="pending">Chờ xử lý</Select.Option>
            <Select.Option value="in-progress">Đang xử lý</Select.Option>
            <Select.Option value="resolved">Đã giải quyết</Select.Option>
            <Select.Option value="rejected">Từ chối</Select.Option>
          </Select>
        </Space>
      ),
    },
  ];

  return (
    <Card 
      bordered={false}
      style={{ 
        margin: '24px',
        borderRadius: '8px',
        boxShadow: '0 1px 2px 0 rgba(0,0,0,0.03)'
      }}
    >
      <div style={{ marginBottom: 24 }}>
        <Title level={4}>Quản lý yêu cầu hỗ trợ</Title>
      </div>

      <Table
        columns={columns}
        dataSource={supports}
        rowKey="FeedbackID"
        loading={loading}
        pagination={{
          defaultPageSize: 10,
          showSizeChanger: true,
          showTotal: (total) => `Tổng số ${total} yêu cầu`,
          pageSizeOptions: ['10', '20', '50']
        }}
        style={{ marginTop: 16 }}
        scroll={{ x: 1000 }}
      />
      
      {selectedSupport && (
        <SupportDetail
          support={selectedSupport}
          visible={!!selectedSupport}
          onClose={() => {
            setSelectedSupport(null);
            loadSupports();
          }}
        />
      )}
    </Card>
  );
};

export default SupportList;